// drip-step-skip-state.ts — the pure skip/unskip decision for a recurring-drip
// step card and the calendar chip menu (#592b skip / #592c undo-skip).
//
// THE CONTRACT
// ------------
// A single step of a recurring drip's current cycle can be:
//   - SKIPPED    ("Skip this message")  when it is NOT already skipped and its
//                                       send time has NOT come (state 'scheduled')
//   - UNSKIPPED  ("Undo skip")          when it IS already skipped and its send
//                                       time has NOT come (state 'scheduled')
//   - neither                           once the step has sent / is in-flight
//                                       (a 'done'/'in-flight' step is past the
//                                       point where skipping it means anything),
//                                       or when the drip is not recurring (no
//                                       cycle to skip a message within).
// The "Skipped" badge is shown whenever the step is skipped, INDEPENDENT of
// whether an undo action is offered — a skipped step that has already passed its
// send time still reads as "Skipped", it just can no longer be undone.
//
// Extracted from sequence-detail.tsx so this decision is unit-tested in node env,
// the way the rest of the campaigns-redesign decisions are.

// The three dispatch states a step card understands. Mirror of the local
// `StepState` in sequence-detail.tsx (kept in sync; a step is 'scheduled' until
// its campaign starts dispatching).
export type StepDispatchState = 'done' | 'in-flight' | 'scheduled';

// The one skip-related affordance a step offers (at most one at a time).
export type StepSkipAction = 'none' | 'skip' | 'unskip';

export interface StepSkipStateInput {
  /** The step's dispatch state. Only 'scheduled' (not-yet-sent) can be
   *  skipped/unskipped; a sent or in-flight step offers neither. */
  state: StepDispatchState;
  /** Whether this step is in `drip.skippedSteps` for the current cycle. */
  skipped: boolean;
  /** Whether single-step skip/unskip is available at all — true only for a
   *  recurring drip with a live rruleset (the backend action requires one).
   *  A non-recurring drip / standalone campaign has no cycle, so no action. */
  skipEnabled: boolean;
}

export interface StepSkipState {
  /** Show the "Skipped" badge. True iff the step is skipped, regardless of
   *  whether it can still be undone. */
  showSkippedBadge: boolean;
  /** Which action to render in the card's action slot / chip menu. */
  action: StepSkipAction;
}

/**
 * Decide, purely, what a recurring-drip step should show for skip/unskip.
 *
 *   not-skipped + scheduled + enabled → offer "Skip this message"
 *   skipped     + scheduled + enabled → offer "Undo skip"   (#592c)
 *   sent / in-flight                  → no action (badge still shows if skipped)
 *   not recurring (disabled)          → no action (badge still shows if skipped)
 */
export const deriveStepSkipState = (input: StepSkipStateInput): StepSkipState => {
  const { state, skipped, skipEnabled } = input;
  const notYetSent = state === 'scheduled';
  let action: StepSkipAction = 'none';
  if (skipEnabled && notYetSent) {
    action = skipped ? 'unskip' : 'skip';
  }
  return { showSkippedBadge: skipped, action };
};

// F3 — the drip statuses in which the backend actually HONORS a current-cycle
// single-step skip. `skipDripStepAction` treats an occurrence as the current
// cycle only for a non-terminal drip (`['active','paused','draft']`); on a
// terminal drip (completed / aborted) the skip is stashed as a pending override
// nothing ever consumes, so the message would still send while the card shows
// "Skipped". Offering the action only in these states keeps FE ⇄ BE aligned.
export const SKIPPABLE_DRIP_STATUSES = ['active', 'paused', 'draft'] as const;

/**
 * F3 — is a drip in a state where a single-step skip/unskip is actually honored
 * by the backend? Only the non-terminal states above; anything else (a terminal
 * or unknown/absent status) returns false so the card offers no misleading skip.
 */
export const isDripStatusSkippable = (status: string | null | undefined): boolean =>
  status != null && (SKIPPABLE_DRIP_STATUSES as readonly string[]).includes(status);

/** A skipped, not-yet-sent step of a recurring drip can be un-skipped. */
export const canUnskipStep = (input: StepSkipStateInput): boolean =>
  deriveStepSkipState(input).action === 'unskip';

/** A not-skipped, not-yet-sent step of a recurring drip can be skipped. */
export const canSkipStep = (input: StepSkipStateInput): boolean =>
  deriveStepSkipState(input).action === 'skip';

/**
 * #592c — the drip's `skippedSteps` after SKIPPING `stepIndex`, as the FE sets it
 * OPTIMISTICALLY on the local drip (the `skipDripStep` mutation's onQueryStarted).
 * Mirrors the backend `$addToSet`: de-duped, order-preserving, idempotent (a
 * no-op when the step is already skipped).
 */
export const addSkippedStep = (
  skippedSteps: number[] | undefined,
  stepIndex: number,
): number[] => [...new Set([...(skippedSteps ?? []), stepIndex])];

/**
 * #592c — the drip's `skippedSteps` after UN-skipping `stepIndex`, as the FE sets
 * it OPTIMISTICALLY on the local drip (the `unskipDripStep` mutation's
 * onQueryStarted). The realtime (MQTT) drip patch is not guaranteed to reach the
 * client (e.g. broker mismatch in local dev, or a dropped/late event), so the
 * mutation writes this array to the store itself rather than waiting for the
 * echo. Mirrors the backend `$pull`: order-preserving, a no-op when the step
 * wasn't skipped (idempotent).
 */
export const removeSkippedStep = (
  skippedSteps: number[] | undefined,
  stepIndex: number,
): number[] => (skippedSteps ?? []).filter((i) => i !== stepIndex);

/** A single-step skip for a cycle that has NOT yet materialized — keyed to the
 *  occurrence START instant (minute granularity is the BE↔client contract). */
export interface PendingStepSkip {
  occurrenceDate: string | number | Date;
  stepIndex: number;
}

/** Same occurrence at MINUTE resolution — the BE↔client matching contract (see
 *  WhatsAppSelectors + the backend skip consumer). */
const sameOccurrenceMinute = (
  a: string | number | Date,
  b: string | number | Date,
): boolean => Math.floor(new Date(a).getTime() / 60000) === Math.floor(new Date(b).getTime() / 60000);

/**
 * #592c — the drip's `pendingStepSkips` after SKIPPING `stepIndex` on a FUTURE
 * occurrence (the calendar chip case), as the FE sets it OPTIMISTICALLY. Mirrors
 * the backend: idempotent by (occurrence-minute, stepIndex). The `occurrenceDate`
 * is stored as passed (an ISO instant), matched by minute on removal.
 */
export const addPendingStepSkip = (
  pending: PendingStepSkip[] | undefined,
  occurrenceDate: string | number | Date,
  stepIndex: number,
): PendingStepSkip[] => {
  const list = pending ?? [];
  const already = list.some(
    (p) => p && p.stepIndex === stepIndex && sameOccurrenceMinute(p.occurrenceDate, occurrenceDate),
  );
  return already ? [...list] : [...list, { occurrenceDate, stepIndex }];
};

/**
 * #592c — the drip's `pendingStepSkips` after UN-skipping `stepIndex` on a FUTURE
 * occurrence. Removes the matching (occurrence-minute, stepIndex) entry; a no-op
 * when absent (idempotent).
 */
export const removePendingStepSkip = (
  pending: PendingStepSkip[] | undefined,
  occurrenceDate: string | number | Date,
  stepIndex: number,
): PendingStepSkip[] =>
  (pending ?? []).filter(
    (p) => !(p && p.stepIndex === stepIndex && sameOccurrenceMinute(p.occurrenceDate, occurrenceDate)),
  );
